import React, { useEffect, useState } from 'react';
import PageWrapper from '../../components/PageWrapper/PageWrapper';
import venueDashboardApiService from '../../services/venueDashboardApi';
import { slugifyCatalogTagLabel } from '../../utils/catalogTags';

const AdminCatalogAddonTags = () => {
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [label, setLabel] = useState('');
  const [slug, setSlug] = useState('');
  const [sortOrder, setSortOrder] = useState('0');
  const [saving, setSaving] = useState(false);

  const loadTags = async () => {
    setLoading(true);
    setError('');
    try {
      const rows = await venueDashboardApiService.getAdminCatalogAddonTags();
      setTags(Array.isArray(rows) ? rows : []);
    } catch (err) {
      setError(err?.message || 'Could not load catalog tags.');
      setTags([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTags();
  }, []);

  const handleCreate = async (event) => {
    event.preventDefault();
    const trimmedLabel = label.trim();
    if (!trimmedLabel) {
      setError('Label is required.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      await venueDashboardApiService.createCatalogAddonTag({
        label: trimmedLabel,
        slug: (slug.trim() || slugifyCatalogTagLabel(trimmedLabel)),
        sort_order: Number.parseInt(sortOrder, 10) || 0,
        is_active: true,
      });
      setLabel('');
      setSlug('');
      setSortOrder('0');
      await loadTags();
    } catch (err) {
      setError(err?.message || 'Could not create tag.');
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (tag) => {
    setSaving(true);
    setError('');
    try {
      await venueDashboardApiService.updateCatalogAddonTag(tag.id, {
        is_active: !tag.is_active,
      });
      await loadTags();
    } catch (err) {
      setError(err?.message || 'Could not update tag.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (tag) => {
    if (!window.confirm(`Delete tag "${tag.label}"? Items will keep the slug but it will no longer be in the catalog.`)) {
      return;
    }
    setSaving(true);
    setError('');
    try {
      await venueDashboardApiService.deleteCatalogAddonTag(tag.id);
      await loadTags();
    } catch (err) {
      setError(err?.message || 'Could not delete tag.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <PageWrapper useNewFooter useLanding2Header>
      <main className="bg-white">
        <div className="mx-auto w-full max-w-[96rem] px-6 py-10 sm:px-8">
          <div className="mb-8">
            <p className="text-[11px] font-medium uppercase tracking-[0.2em] text-gray-500">Admin</p>
            <h1 className="font-heading mt-2 text-3xl font-bold text-gray-900">Catalog tags</h1>
            <p className="mt-2 max-w-2xl text-sm text-gray-600">
              Tags like Food and Beverage can be attached to packages and add-ons in the venue dashboard.
              Organizers filter by these tags on the booking page.
            </p>
          </div>

          {error ? <p className="mb-4 text-sm text-red-700">{error}</p> : null}

          <form onSubmit={handleCreate} className="mb-8 grid gap-3 rounded-xl border border-gray-200 p-5 sm:grid-cols-4">
            <label className="block sm:col-span-2">
              <span className="mb-1 block text-sm font-medium text-gray-700">Label</span>
              <input
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                placeholder="Food"
              />
            </label>
            <label className="block">
              <span className="mb-1 block text-sm font-medium text-gray-700">Slug (optional)</span>
              <input
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                value={slug}
                onChange={(e) => setSlug(e.target.value)}
                placeholder="food"
              />
            </label>
            <label className="block">
              <span className="mb-1 block text-sm font-medium text-gray-700">Sort order</span>
              <input
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                type="number"
                value={sortOrder}
                onChange={(e) => setSortOrder(e.target.value)}
              />
            </label>
            <div className="sm:col-span-4">
              <button
                type="submit"
                disabled={saving}
                className="rounded-lg bg-[#046242] px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"
              >
                {saving ? 'Saving…' : 'Add tag'}
              </button>
            </div>
          </form>

          {loading ? (
            <p className="text-sm text-gray-600">Loading tags…</p>
          ) : (
            <div className="overflow-x-auto rounded-xl border border-gray-200">
              <table className="min-w-full text-left text-sm">
                <thead className="border-b border-gray-200 bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
                  <tr>
                    <th className="px-4 py-3">Label</th>
                    <th className="px-4 py-3">Slug</th>
                    <th className="px-4 py-3">Order</th>
                    <th className="px-4 py-3">Active</th>
                    <th className="px-4 py-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {tags.map((tag) => (
                    <tr key={tag.id} className="border-b border-gray-100">
                      <td className="px-4 py-3 font-medium text-gray-900">{tag.label}</td>
                      <td className="px-4 py-3 font-mono text-xs text-gray-600">{tag.slug}</td>
                      <td className="px-4 py-3">{tag.sort_order ?? 0}</td>
                      <td className="px-4 py-3">{tag.is_active ? 'Yes' : 'No'}</td>
                      <td className="px-4 py-3">
                        <div className="flex gap-2">
                          <button
                            type="button"
                            disabled={saving}
                            className="rounded border border-gray-300 px-2 py-1 text-xs font-semibold"
                            onClick={() => toggleActive(tag)}
                          >
                            {tag.is_active ? 'Deactivate' : 'Activate'}
                          </button>
                          <button
                            type="button"
                            disabled={saving}
                            className="rounded border border-red-200 px-2 py-1 text-xs font-semibold text-red-700"
                            onClick={() => handleDelete(tag)}
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>
    </PageWrapper>
  );
};

export default AdminCatalogAddonTags;
